import {
    Box,
    Pagination,
    QuickFilterButton,
    showToast,
    Table,
    TabMenu,
    TabMenuItem,
    TextBlock,
} from "@bcc-ui/ui-components";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { createColumns } from "./CreateValueRecoveryColumns";
import {
    addMissingUuids,
    buildGradingExportRequest,
    buildGradingTablePageRequest,
    buildGradingTableQuickFilters,
    buildGradingTableSearchRequest,
    formatDeviceTypeLabel,
    GradeTab,
    GRADING_TABLE_PAGE_SIZE,
    replaceAssetValues,
} from "./gradingTableFilters";
import { ValueRecoveryActionBar } from "./value-recovery-action-bar/ValueRecoveryActionBar";
import style from "./value-recovery-step.scss";
import AssetSidePanel from "components/asset-management-system/AssetSidePanel";
import { EstimatedValueCard } from "components/asset-management-system/custom-views/tabs/value/steps/value-recovery-step/estimated-value-card/EstimatedValueCard";
import { GradeBadge } from "components/asset-management-system/custom-views/tabs/value/steps/value-recovery-step/grade-badge/GradeBadge";
import { RerunGradingDialog } from "components/asset-management-system/custom-views/tabs/value/steps/value-recovery-step/rerun-grading-dialog/RerunGradingDialog";
import { UnlockRemainingFleetValueCard } from "components/asset-management-system/custom-views/tabs/value/steps/value-recovery-step/unlock-remaining-fleet-value-card/UnlockRemainingFleetValueCard";
import { SmartGradingBanner } from "components/asset-management-system/custom-views/tabs/value/steps/value-step-card/smart-grading-banner/SmartGradingBanner";
import { TableFooter } from "components/table/TableFooter";
import { AUTH_ASSET_MANIFEST_VIEW } from "domain/authority";
import { isGrade } from "domain/grade";
import {
    assetGradingService,
    AssetValues,
    GradingStep,
    GradingStepProgress,
    toStepperNumber,
    WorkflowForGrading,
} from "services/asset-management/AssetGradingService";
import { Asset, AssetSearchRequest, assetService, GradedSummary } from "services/asset-management/AssetService";
import { userSessionService } from "services/user/UserSessionService";
import { logger } from "utils/logging";
import { RepositoryKey } from "utils/repository";

const MAX_PAGE_NUMBERS_TO_SHOW = 3;
const GRADING_STUCK_THRESHOLD_MILLISECONDS = 3 * 60 * 60 * 1000;

const NO_GRADE_ESTIMATED_VALUES: AssetValues = { value: 0, upliftValue: 0 };

export function isGradingStuck(editedDate: string | undefined, nowMilliseconds: number = Date.now()): boolean {
    if (!editedDate) {
        return false;
    }
    const editedDateMs = new Date(editedDate).getTime();
    const differenceMs = nowMilliseconds - editedDateMs;
    return differenceMs >= GRADING_STUCK_THRESHOLD_MILLISECONDS;
}

export function computeIsRerunAllowed(
    gradedSummary: GradedSummary | undefined,
    editedDate: string | undefined,
    isSmartGradingEnabled: boolean
): boolean {
    if (!isSmartGradingEnabled || !gradedSummary) {
        return false;
    }
    return gradedSummary.totalPending === 0 || isGradingStuck(editedDate);
}

export type HeaderSelectionState = "none" | "some" | "all";

export function computeHeaderSelectionState(pageAssets: { checked?: boolean }[]): HeaderSelectionState {
    if (pageAssets.length === 0) {
        return "none";
    }
    if (pageAssets.every((asset) => asset.checked === true)) {
        return "all";
    }
    if (pageAssets.some((asset) => asset.checked === true)) {
        return "some";
    }
    return "none";
}

interface Props {
    assetSearchRequest?: AssetSearchRequest;
    refreshKey?: number;
    onResolveAssetRisks?: () => void;
    isSmartGradingEnabled: boolean;
    eligibleAssetsCount: number;
    handleSmartGradingChange: (value: boolean) => void;
    handleGradingStepSave: (step: GradingStep, enabled: boolean) => void;
    isLaunchingGrading?: boolean;
    workflows: WorkflowForGrading[];
    templates: WorkflowForGrading[];
    workflowUuid?: string;
    onRerunGrading?: (workflowUuid: string, templateSelected: boolean) => void;
    isSmartGradingLicenseAvailable: boolean;
    isGradingBannerToggleDisabled?: boolean;
}

interface GradeTabMenuItem {
    tab: GradeTab;
    label: string;
}

type SelectableAsset = Asset & { checked?: boolean };

export const ValueRecoveryStep = (props: Props) => {
    const { t } = useTranslation();

    const [assets, setAssets] = useState<Asset[]>([]);
    const [matchingAssetsCount, setMatchingAssetsCount] = useState(0);
    const [selectedAssetUuids, setSelectedAssetUuids] = useState<string[]>([]);
    const [assetsReloadCount, setAssetsReloadCount] = useState(0);
    const [totalAssets, setTotalAssets] = useState<number>();
    const [isSearching, setIsSearching] = useState(false);
    const [isApplyingBulkAction, setIsApplyingBulkAction] = useState(false);
    const [selectedDeviceType, setSelectedDeviceType] = useState<string>();
    const [assetTypes, setAssetTypes] = useState<string[]>([]);
    const [gradedSummary, setGradedSummary] = useState<GradedSummary>();
    const [estimatedValues, setEstimatedValues] = useState<AssetValues>();
    const [isEstimatedValueLoading, setIsEstimatedValueLoading] = useState(false);
    const [gradingStepProgress, setGradingStepProgress] = useState<GradingStepProgress>();
    const [activeTab, setActiveTab] = useState<GradeTab>(GradeTab.ALL);
    const [currentPage, setCurrentPage] = useState(1);
    const [isRerunDialogOpen, setIsRerunDialogOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState<Asset>();
    const [isAssetDetailsLoading, setIsAssetDetailsLoading] = useState(false);
    const [estimatedValuesReloadCount, setEstimatedValuesReloadCount] = useState(0);
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const assetDetailsAbortControllerRef = useRef<AbortController>();
    const scrollTopBeforeSelectionChangeRef = useRef<number>();

    const totalPages = Math.max(1, Math.ceil(matchingAssetsCount / GRADING_TABLE_PAGE_SIZE));
    const paginatedAssets: SelectableAsset[] = assets.map((asset) => ({
        ...asset,
        checked: selectedAssetUuids.includes(asset.uuid),
    }));
    const headerSelectionState = computeHeaderSelectionState(paginatedAssets);

    const gradeTabs: GradeTabMenuItem[] = [
        { tab: GradeTab.ALL, label: t("Common.all") },
        {
            tab: GradeTab.GRADE_A,
            label: t("AssetManagement.value.gradingTable.gradeHeader"),
        },
        {
            tab: GradeTab.GRADE_B,
            label: t("AssetManagement.value.gradingTable.gradeHeader"),
        },
        {
            tab: GradeTab.GRADE_C,
            label: t("AssetManagement.value.gradingTable.gradeHeader"),
        },
        {
            tab: GradeTab.GRADE_D,
            label: t("AssetManagement.value.gradingTable.gradeHeader"),
        },
        { tab: GradeTab.NO_GRADE, label: t("AssetManagement.value.gradingTable.noGrade") },
    ];

    const updateSelection = (updater: (previousUuids: string[]) => string[]) => {
        scrollTopBeforeSelectionChangeRef.current = tableContainerRef.current?.scrollTop;
        setSelectedAssetUuids(updater);
    };

    useLayoutEffect(() => {
        const scrollTop = scrollTopBeforeSelectionChangeRef.current;
        scrollTopBeforeSelectionChangeRef.current = undefined;
        if (scrollTop !== undefined && tableContainerRef.current) {
            tableContainerRef.current.scrollTop = scrollTop;
        }
    });

    const toggleAssetSelection = (uuid: string) => {
        updateSelection((previousUuids) =>
            previousUuids.includes(uuid)
                ? previousUuids.filter((selectedUuid) => selectedUuid !== uuid)
                : [...previousUuids, uuid]
        );
    };

    const toggleSelectAllOnPage = () => {
        const pageUuids = paginatedAssets.map((asset) => asset.uuid);
        const allPageAssetsSelected = paginatedAssets.every((asset) => asset.checked === true);
        updateSelection((previousUuids) =>
            allPageAssetsSelected
                ? previousUuids.filter((uuid) => !pageUuids.includes(uuid))
                : addMissingUuids(previousUuids, pageUuids)
        );
    };

    const deselectAll = () => {
        updateSelection(() => []);
    };

    const selectAllOnPage = () => {
        const pageUuids = paginatedAssets.map((asset) => asset.uuid);
        updateSelection((previousUuids) => addMissingUuids(previousUuids, pageUuids));
    };

    const selectGradeTab = (gradeTab: GradeTab) => {
        setActiveTab(gradeTab);
        setCurrentPage(1);
    };

    const selectDeviceType = (deviceType?: string) => {
        setSelectedDeviceType(deviceType);
        setCurrentPage(1);
    };

    const exportAssets = () => {
        const quickFilters = buildGradingTableQuickFilters(activeTab, selectedDeviceType);
        const manifestUuid = userSessionService.userHasAllAuthorities([AUTH_ASSET_MANIFEST_VIEW])
            ? props.assetSearchRequest?.groupUuid
            : undefined;
        setIsExporting(true);
        assetService
            .exportAssetsBySearchRequest(
                buildGradingExportRequest(props.assetSearchRequest, quickFilters),
                manifestUuid
            )
            .then(() => showToast(t("AssetManagement.exportAssets.message", { count: matchingAssetsCount }), "info"))
            .catch((error: Error) => {
                logger.error("Failed to export assets: ", error);
                showToast(t("AssetManagement.requestFailed"), "info");
            })
            .finally(() => setIsExporting(false));
    };

    const reloadAssets = () => {
        deselectAll();
        setAssetsReloadCount((previous) => previous + 1);
    };

    useEffect(() => {
        setSelectedAssetUuids([]);
    }, [props.assetSearchRequest, activeTab, selectedDeviceType]);

    useEffect(() => {
        const abortController = new AbortController();
        const quickFilters = buildGradingTableQuickFilters(activeTab, selectedDeviceType);
        setIsSearching(true);
        assetService
            .searchAssets(
                buildGradingTablePageRequest(props.assetSearchRequest, quickFilters, currentPage),
                abortController
            )
            .then((response) => {
                if (response.assets.length === 0 && currentPage > 1) {
                    setCurrentPage(1);
                    return;
                }
                setAssets(response.assets);
                setMatchingAssetsCount(response.totalAssets ?? response.assets.length);
                if (selectedDeviceType === undefined) {
                    setAssetTypes(response.assetTypes);
                }
                if (quickFilters.length === 0) {
                    setTotalAssets(response.totalAssets);
                }
            })
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to fetch assets for the grading table: ", error);
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setIsSearching(false);
                }
            });
        return () => abortController.abort();
    }, [props.assetSearchRequest, activeTab, selectedDeviceType, currentPage, assetsReloadCount]);

    useEffect(() => {
        const abortController = new AbortController();
        assetGradingService
            .fetchGradedSummary(props.assetSearchRequest, abortController)
            .then((summary: GradedSummary) => setGradedSummary(summary))
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to fetch graded summary for value recovery: ", error);
                }
            });
        return () => abortController.abort();
    }, [props.assetSearchRequest]);

    useEffect(() => {
        if (props.assetSearchRequest?.groupUuid === undefined) {
            setEstimatedValues(undefined);
            setIsEstimatedValueLoading(false);
            return;
        }
        if (activeTab === GradeTab.NO_GRADE) {
            setEstimatedValues(NO_GRADE_ESTIMATED_VALUES);
            setIsEstimatedValueLoading(false);
            return;
        }
        const abortController = new AbortController();
        const quickFilters = buildGradingTableQuickFilters(activeTab, selectedDeviceType);
        setIsEstimatedValueLoading(true);
        assetGradingService
            .fetchAssetValues(buildGradingTableSearchRequest(props.assetSearchRequest, quickFilters), abortController)
            .then((values: AssetValues) => setEstimatedValues(values))
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    setEstimatedValues(undefined);
                    logger.error("Failed to fetch estimated value for value recovery: ", error);
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setIsEstimatedValueLoading(false);
                }
            });
        return () => abortController.abort();
    }, [props.assetSearchRequest, activeTab, selectedDeviceType, estimatedValuesReloadCount]);

    useEffect(() => {
        if (!props.assetSearchRequest?.groupUuid || !gradedSummary || gradedSummary.totalPending === 0) {
            return;
        }
        const abortController = new AbortController();
        assetGradingService
            .fetchGradingStepProgress(props.assetSearchRequest.groupUuid, abortController)
            .then((progress: GradingStepProgress | undefined) => {
                if (progress) {
                    setGradingStepProgress(progress);
                }
            })
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to fetch grading step progress for value recovery: ", error);
                }
            });
        return () => abortController.abort();
    }, [props.assetSearchRequest?.groupUuid, gradedSummary]);

    const closeSidePanel = () => {
        assetDetailsAbortControllerRef.current?.abort();
        setSelectedAsset(undefined);
        setIsAssetDetailsLoading(false);
    };

    const fetchSelectedAsset = (assetUuid: string, onFailure: () => void) => {
        assetDetailsAbortControllerRef.current?.abort();
        const abortController = new AbortController();
        assetDetailsAbortControllerRef.current = abortController;
        setIsAssetDetailsLoading(true);
        assetService
            .fetchAssetByUuid(assetUuid, abortController)
            .then((assetDetails) => {
                if (!abortController.signal.aborted) {
                    setSelectedAsset((previous) => (previous?.uuid === assetUuid ? assetDetails : previous));
                }
            })
            .catch((error: Error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to fetch asset details for the side panel: ", error);
                    showToast(t("AssetManagement.requestFailed"), "info");
                    onFailure();
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setIsAssetDetailsLoading(false);
                }
            });
    };

    const openSidePanel = (asset: Asset) => {
        setSelectedAsset(asset);
        fetchSelectedAsset(asset.uuid, () => setSelectedAsset(undefined));
    };

    // The panel changes the asset, for example when an optimization suggestion is selected, so the rows and the
    // estimated value, which are loaded from the same data, are updated too.
    const handleAssetUpdated = (updated: Asset) => {
        setSelectedAsset((previous) => (previous?.uuid === updated.uuid ? updated : previous));
        setAssets((previous) => replaceAssetValues(previous, updated));
        setEstimatedValuesReloadCount((previous) => previous + 1);
    };

    useEffect(() => {
        if (selectedAsset !== undefined) {
            fetchSelectedAsset(selectedAsset.uuid, () => {});
        }
    }, [props.refreshKey]);

    useEffect(() => () => assetDetailsAbortControllerRef.current?.abort(), []);

    const columns = createColumns(toggleAssetSelection, toggleSelectAllOnPage, paginatedAssets, openSidePanel);

    const tableTabMenu = (
        <TabMenu>
            {gradeTabs.map((gradeTab, index) => {
                const isActive = activeTab === gradeTab.tab;
                return (
                    <TabMenuItem key={index} isActive={isActive} onClick={() => selectGradeTab(gradeTab.tab)}>
                        <Box verticalAlignment={"Center"} gap={"Xxxs"}>
                            <TextBlock
                                disableBottomSpacing
                                className={!isActive ? style.inActiveTabMenuItem : undefined}
                            >
                                {gradeTab.label}
                            </TextBlock>
                            {gradeTab.tab !== GradeTab.ALL && isGrade(gradeTab.tab) && (
                                <>
                                    <GradeBadge grade={gradeTab.tab} size={16} />
                                </>
                            )}
                        </Box>
                    </TabMenuItem>
                );
            })}
        </TabMenu>
    );

    const handleOpenRerunDialog = () => {
        setIsRerunDialogOpen(true);
    };

    const handleCloseRerunDialog = () => {
        setIsRerunDialogOpen(false);
    };

    const handleConfirmRerunGrading = (workflowUuid: string, templateSelected: boolean) => {
        props.onRerunGrading?.(workflowUuid, templateSelected);
        setIsRerunDialogOpen(false);
    };

    return (
        <>
            <SmartGradingBanner
                isSmartGradingLicenseAvailable={props.isSmartGradingLicenseAvailable}
                currentStepNumber={toStepperNumber(GradingStep.VALUE_RECOVERY)}
                isSmartGradingEnabled={props.isSmartGradingEnabled}
                eligibleAssetsCount={props.eligibleAssetsCount}
                onSmartGradingChange={props.handleSmartGradingChange}
                onGradingStepSave={props.handleGradingStepSave}
                isRerunAllowed={
                    props.isSmartGradingLicenseAvailable &&
                    computeIsRerunAllowed(gradedSummary, gradingStepProgress?.editedDate, props.isSmartGradingEnabled)
                }
                handleRerunGrading={handleOpenRerunDialog}
                isGradingBannerToggleDisabled={props.isGradingBannerToggleDisabled}
            />
            <RerunGradingDialog
                isOpen={isRerunDialogOpen}
                isLaunching={props.isLaunchingGrading ?? false}
                assetsToRegradeCount={totalAssets ?? 0}
                workflows={props.workflows}
                templates={props.templates}
                initialWorkflowUuid={props.workflowUuid}
                onClose={handleCloseRerunDialog}
                onConfirm={handleConfirmRerunGrading}
            />
            <Box gap="Sm" verticalAlignment="Top" className={style.cardsContainer}>
                <Box columnLayout gap="Xs" className={style.gradingTableWrapper}>
                    {tableTabMenu}
                    <Box verticalAlignment="Center" flexWrap="Wrap" justifyContent={"SpaceBetween"}>
                        <Box gap="Xxs" flexWrap="Wrap">
                            {[undefined, ...assetTypes].map((deviceType) => (
                                <QuickFilterButton
                                    key={deviceType ?? "all"}
                                    isActive={selectedDeviceType === deviceType}
                                    onClick={() => selectDeviceType(deviceType)}
                                >
                                    {deviceType === undefined ? t("Common.all") : formatDeviceTypeLabel(deviceType)}
                                </QuickFilterButton>
                            ))}
                        </Box>
                    </Box>

                    <Box className={style.tableContainer}>
                        <Table
                            key={`${headerSelectionState}-${currentPage}`}
                            columns={columns}
                            data={paginatedAssets}
                            loaded={!isSearching && !isApplyingBulkAction}
                            tableIdentity={RepositoryKey.GRADING_TABLE}
                            emptyStateMessage={t("AssetManagement.value.gradingTable.emptyStateMessage")}
                            containerRef={tableContainerRef}
                            isSidePanelOpen={selectedAsset !== undefined}
                            sidePanel={
                                selectedAsset ? (
                                    <AssetSidePanel
                                        asset={selectedAsset}
                                        onClose={closeSidePanel}
                                        isLoading={isAssetDetailsLoading}
                                        onAssetUpdated={handleAssetUpdated}
                                    />
                                ) : (
                                    <></>
                                )
                            }
                        />
                        {selectedAssetUuids.length > 0 && (
                            <ValueRecoveryActionBar
                                selectedCount={selectedAssetUuids.length}
                                pageAssetCount={paginatedAssets.length}
                                selectedAssetUuids={selectedAssetUuids}
                                deselectAll={deselectAll}
                                selectAllOnPage={selectAllOnPage}
                                onActionApplied={reloadAssets}
                                onApplyingChange={setIsApplyingBulkAction}
                            />
                        )}
                    </Box>
                    {assets.length > 0 && (
                        <TableFooter
                            className={style.tableFooter}
                            rightColumn={
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    maxPageNumbersToShow={MAX_PAGE_NUMBERS_TO_SHOW}
                                    handlePageChange={(pageNumber) => {
                                        setCurrentPage(pageNumber);
                                        tableContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
                                    }}
                                    paginationText={t("AssetManagement.showingAssets", {
                                        start: (currentPage - 1) * GRADING_TABLE_PAGE_SIZE + 1,
                                        end: (currentPage - 1) * GRADING_TABLE_PAGE_SIZE + paginatedAssets.length,
                                        total: matchingAssetsCount,
                                    })}
                                />
                            }
                        />
                    )}
                </Box>
                {(estimatedValues !== undefined || isEstimatedValueLoading || gradedSummary !== undefined) && (
                    <Box columnLayout className={style.fleetValueSummaryRail}>
                        {(estimatedValues !== undefined || isEstimatedValueLoading) && (
                            <EstimatedValueCard
                                values={estimatedValues}
                                isLoading={isEstimatedValueLoading}
                                isExporting={isExporting}
                                onExport={exportAssets}
                            />
                        )}
                        {gradedSummary !== undefined && totalAssets !== undefined && (
                            <UnlockRemainingFleetValueCard
                                data={gradedSummary}
                                totalAssets={totalAssets}
                                onResolveAssetRisks={props.onResolveAssetRisks}
                            />
                        )}
                    </Box>
                )}
            </Box>
        </>
    );
};
